const esc = (s) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
const fmt = (n) => `$${Number(n || 0).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const STYLE = `
  * { box-sizing: border-box; }
  body { font-family: 'Helvetica Neue', Arial, sans-serif; color: #111; margin: 0; padding: 28px; font-size: 13px; }
  .doc { max-width: 720px; margin: 0 auto; page-break-after: always; }
  .head { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 2px solid #C9A24B; padding-bottom: 14px; margin-bottom: 18px; }
  .brand { font-size: 19px; font-weight: 700; letter-spacing: 1px; }
  .brand small { display: block; font-weight: 400; color: #666; font-size: 11px; margin-top: 3px; letter-spacing: 0.5px; }
  .doctitle { text-align: right; }
  .doctitle h1 { margin: 0; font-size: 21px; letter-spacing: 2px; color: #C9A24B; }
  .doctitle p { margin: 3px 0 0; color: #555; font-size: 12px; }
  .cols { display: flex; justify-content: space-between; gap: 24px; margin-bottom: 16px; }
  .cols h3 { margin: 0 0 4px; font-size: 10px; text-transform: uppercase; letter-spacing: 1.5px; color: #999; }
  .cols p { margin: 1px 0; }
  table { width: 100%; border-collapse: collapse; margin: 10px 0 14px; }
  th { text-align: left; font-size: 10px; text-transform: uppercase; letter-spacing: 1px; color: #888; border-bottom: 1px solid #ddd; padding: 6px 4px; }
  td { padding: 7px 4px; border-bottom: 1px solid #eee; }
  .r { text-align: right; }
  .totals { margin-left: auto; width: 260px; }
  .totals td { border: none; padding: 4px; }
  .grand td { border-top: 2px solid #C9A24B; font-weight: 700; font-size: 15px; padding-top: 8px; }
  .note { color: #777; font-size: 11px; margin-top: 16px; border-top: 1px solid #eee; padding-top: 10px; }
  .paybox { border: 1px solid #C9A24B; border-radius: 6px; padding: 12px 14px; margin-top: 14px; }
  .paybox h3 { margin: 0 0 6px; font-size: 11px; text-transform: uppercase; letter-spacing: 1.5px; color: #C9A24B; }
  .paybox p { margin: 2px 0; }
  @media print { body { padding: 10mm; } }
`;

function openPrint(title, bodies) {
  const html = `<!DOCTYPE html>
<html><head><meta charset="utf-8"><title>${esc(title)}</title><style>${STYLE}</style></head>
<body>${bodies.join('\n')}</body></html>`;
  const prev = document.getElementById('alp-payroll-print-frame');
  if (prev) prev.remove();
  const frame = document.createElement('iframe');
  frame.id = 'alp-payroll-print-frame';
  frame.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden;';
  document.body.appendChild(frame);
  frame.onload = () => {
    const win = frame.contentWindow;
    win.onafterprint = () => setTimeout(() => frame.remove(), 500);
    setTimeout(() => { win.focus(); win.print(); }, 150);
  };
  frame.srcdoc = html;
  return true;
}

const header = (biz, title, subtitle) => `
  <div class="head">
    <div class="brand">${esc(biz.businessName || 'Australian Luxe Peptides')}
      <small>${biz.businessABN ? `ABN ${esc(biz.businessABN)}` : ''}${biz.businessAddress ? ` &middot; ${esc(biz.businessAddress)}` : ''}</small>
    </div>
    <div class="doctitle"><h1>${esc(title)}</h1><p>${esc(subtitle)}</p></div>
  </div>`;

function payslipBody(run, line, biz) {
  return `<div class="doc">
    ${header(biz, 'PAYSLIP', `${esc(run.runNumber)} · ${esc(run.frequency)}`)}
    <div class="cols">
      <div><h3>Employee</h3><p><strong>${esc(line.name)}</strong></p>${line.role ? `<p>${esc(line.role)}</p>` : ''}</div>
      <div><h3>Pay period</h3><p>${esc(run.periodStart)} &mdash; ${esc(run.periodEnd)}</p><p>Paid: ${esc((run.paidAt || run.createdAt || '').slice(0, 10))}</p></div>
    </div>
    <table>
      <tr><th>Description</th><th class="r">Amount</th></tr>
      <tr><td>Gross pay${line.payType === 'hourly' && line.hours ? ` (${line.hours} hrs)` : ''}</td><td class="r">${fmt(line.gross)}</td></tr>
      <tr><td>PAYG tax withheld</td><td class="r">&minus;${fmt(line.tax)}</td></tr>
    </table>
    <table class="totals">
      <tr class="grand"><td>NET PAY</td><td class="r">${fmt(line.net)}</td></tr>
      <tr><td>Superannuation (${line.superRate || 12}%, paid to fund)</td><td class="r">${fmt(line.super)}</td></tr>
    </table>
    ${line.bankAccountNumber ? `<div class="paybox"><h3>Paid to</h3><p>${esc(line.bankAccountName)} &middot; BSB ${esc(line.bankBSB)} &middot; Acc ${esc(line.bankAccountNumber)}</p></div>` : ''}
    <p class="note">PAYG withholding is an estimate based on ATO Schedule 1 (tax-free threshold claimed). Superannuation is in addition to gross pay. This payslip is generated by ${esc(biz.businessName || 'Australian Luxe Peptides')}.</p>
  </div>`;
}

function invoiceBody(inv, biz) {
  const rows = (inv.items || []).map((i) => `
    <tr><td>${esc(i.description)}</td><td class="r">${i.qty}</td><td class="r">${fmt(i.unitPrice)}</td><td class="r">${fmt(i.qty * i.unitPrice)}</td></tr>`).join('');
  return `<div class="doc">
    ${header(biz, inv.gstEnabled ? 'TAX INVOICE' : 'INVOICE', `${esc(inv.invoiceNumber)}`)}
    <div class="cols">
      <div><h3>Bill to</h3><p><strong>${esc(inv.clientName)}</strong></p>${inv.clientAddress ? `<p>${esc(inv.clientAddress)}</p>` : ''}${inv.clientEmail ? `<p>${esc(inv.clientEmail)}</p>` : ''}</div>
      <div><h3>Dates</h3><p>Issued: ${esc(inv.issueDate)}</p>${inv.dueDate ? `<p>Due: <strong>${esc(inv.dueDate)}</strong></p>` : ''}${inv.costCentre ? `<p>Cost centre: ${esc(inv.costCentre)}</p>` : ''}</div>
    </div>
    <table>
      <tr><th>Description</th><th class="r">Qty</th><th class="r">Unit</th><th class="r">Amount</th></tr>
      ${rows}
    </table>
    <table class="totals">
      <tr><td>Subtotal</td><td class="r">${fmt(inv.subtotal)}</td></tr>
      ${inv.gstEnabled ? `<tr><td>GST (10%)</td><td class="r">${fmt(inv.gst)}</td></tr>` : ''}
      <tr class="grand"><td>TOTAL AUD</td><td class="r">${fmt(inv.total)}</td></tr>
    </table>
    ${biz.bankAccountNumber ? `<div class="paybox"><h3>Payment details</h3>
      <p>Account Name: <strong>${esc(biz.bankAccountName)}</strong></p>
      <p>BSB: <strong>${esc(biz.bankBSB)}</strong> &middot; Account: <strong>${esc(biz.bankAccountNumber)}</strong></p>
      <p>Reference: <strong>${esc(inv.invoiceNumber)}</strong></p></div>` : ''}
    ${inv.notes ? `<p class="note">${esc(inv.notes)}</p>` : ''}
  </div>`;
}

export function printPayslip(run, line, biz) {
  return openPrint(`Payslip ${run.runNumber} — ${line.name}`, [payslipBody(run, line, biz || {})]);
}

export function printPayslips(run, biz) {
  return openPrint(`Payslips ${run.runNumber}`, (run.lines || []).map((l) => payslipBody(run, l, biz || {})));
}

export function printInvoice(inv, biz) {
  return openPrint(`Invoice ${inv.invoiceNumber}`, [invoiceBody(inv, biz || {})]);
}

export function printTaxPack(p, biz) {
  const b = biz || {};
  const qRows = p.quarters.map((q) => `
    <tr><td>${esc(q.label)}</td><td class="r">${fmt(q.g1)}</td><td class="r">${fmt(q.gstOnSales)}</td><td class="r">${fmt(q.purchases)}</td><td class="r">${fmt(q.gstCredits)}</td><td class="r">${fmt(q.netGst)}</td><td class="r">${fmt(q.wages)}</td><td class="r">${fmt(q.payg)}</td></tr>`).join('');
  const catRows = p.spendingCategories.map((c) => `<tr><td>${esc(c.name)} (${c.count})</td><td class="r">${fmt(c.gst)}</td><td class="r">${fmt(c.total)}</td></tr>`).join('');
  const ccRows = p.costCentres.map((c) => `<tr><td>${esc(c.name)} (${c.count})</td><td class="r">${fmt(c.gst)}</td><td class="r">${fmt(c.total)}</td></tr>`).join('');
  const body = `<div class="doc">
    ${header(b, 'TAX PACK', `${esc(p.fyLabel)} · ${esc(p.start)} — ${esc(p.end)}`)}
    <table>
      <tr><th>Summary</th><th class="r">Amount</th></tr>
      <tr><td>Store sales (${p.storeOrders} paid orders, GST incl.)</td><td class="r">${fmt(p.storeSales)}</td></tr>
      <tr><td>Invoice income (${p.invoiceCount} paid invoices)</td><td class="r">${fmt(p.invoiceSales)}</td></tr>
      <tr><td><strong>Total sales (G1)</strong></td><td class="r"><strong>${fmt(p.totalSales)}</strong></td></tr>
      <tr><td>GST on sales (1A)</td><td class="r">${fmt(p.gstOnSales)}</td></tr>
      <tr><td>Purchases / spendings (${p.billCount})</td><td class="r">${fmt(p.purchases)}</td></tr>
      <tr><td>GST credits on purchases (1B)</td><td class="r">${fmt(p.gstOnPurchases)}</td></tr>
      <tr><td><strong>Net GST ${p.netGst >= 0 ? 'payable' : 'refund'}</strong></td><td class="r"><strong>${fmt(Math.abs(p.netGst))}</strong></td></tr>
      <tr><td>Wages gross (W1)</td><td class="r">${fmt(p.wagesGross)}</td></tr>
      <tr><td>PAYG withheld (W2)</td><td class="r">${fmt(p.paygWithheld)}</td></tr>
      <tr><td>Superannuation paid</td><td class="r">${fmt(p.superPaid)}</td></tr>
    </table>
    <h3 style="font-size:11px;text-transform:uppercase;letter-spacing:1.5px;color:#999;margin:18px 0 4px">Quarterly breakdown</h3>
    <table>
      <tr><th>Quarter</th><th class="r">G1</th><th class="r">1A</th><th class="r">Purchases</th><th class="r">1B</th><th class="r">Net GST</th><th class="r">Wages</th><th class="r">PAYG</th></tr>
      ${qRows}
    </table>
    ${catRows ? `<h3 style="font-size:11px;text-transform:uppercase;letter-spacing:1.5px;color:#999;margin:18px 0 4px">Spending by category</h3>
    <table><tr><th>Category</th><th class="r">GST</th><th class="r">Total</th></tr>${catRows}</table>` : ''}
    ${ccRows ? `<h3 style="font-size:11px;text-transform:uppercase;letter-spacing:1.5px;color:#999;margin:18px 0 4px">Invoice income by cost centre</h3>
    <table><tr><th>Cost centre</th><th class="r">GST</th><th class="r">Total</th></tr>${ccRows}</table>` : ''}
    <p class="note">Estimates generated from store records. Store prices are treated as GST-inclusive (GST = total &divide; 11). The CSV export contains full line-by-line detail. Confirm all figures with your accountant.</p>
  </div>`;
  return openPrint(`Tax Pack ${p.fyLabel}`, [body]);
}

export function printBas(q, biz) {
  const b = biz || {};
  const body = `<div class="doc">
    ${header(b, 'BAS SUMMARY', `${esc(q.label)}`)}
    <p style="color:#555">Period: ${esc(q.start)} &mdash; ${esc(q.end)}</p>
    <table>
      <tr><th>Field</th><th></th><th class="r">Amount</th></tr>
      <tr><td><strong>G1</strong> Total sales (GST inclusive)</td><td>store ${fmt(q.storeSales)} + invoices ${fmt(q.invoiceSales)}</td><td class="r">${fmt(q.g1Sales)}</td></tr>
      <tr><td><strong>1A</strong> GST on sales</td><td></td><td class="r">${fmt(q.gstOnSales1A)}</td></tr>
      <tr><td><strong>G11</strong> Purchases / spendings</td><td>${q.billCount} bills</td><td class="r">${fmt(q.purchases)}</td></tr>
      <tr><td><strong>1B</strong> GST credits on purchases</td><td></td><td class="r">${fmt(q.gstOnPurchases1B)}</td></tr>
      <tr><td><strong>W1</strong> Wages paid</td><td></td><td class="r">${fmt(q.wagesW1)}</td></tr>
      <tr><td><strong>W2</strong> PAYG withheld</td><td></td><td class="r">${fmt(q.paygW2)}</td></tr>
    </table>
    <table class="totals">
      <tr class="grand"><td>${q.netGst >= 0 ? 'NET GST PAYABLE' : 'NET GST REFUND'}</td><td class="r">${fmt(Math.abs(q.netGst))}</td></tr>
    </table>
    <p class="note">Estimates generated from your store records. Store prices are treated as GST-inclusive (GST = total &divide; 11). Confirm figures with your accountant before lodging with the ATO.</p>
  </div>`;
  return openPrint(`BAS ${q.label}`, [body]);
}
